import React, { useEffect } from 'react';
import { Box, Typography, Container, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  GlassColors,
  GlassShadows,
  NeumorphicButton,
} from '../../components/glassmorphism/GlassmorphismStyles';

import { ReactComponent as CaseIcon } from '../../assets/icons/info/caseIcon.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import CompareBlock from './CopareBlock';
import { StepsPath } from './StepsPath';
import { trackEvent } from '../../analytics';

const ForProPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    trackEvent('for_pro_page_view');
  }, []);

  const goHandymanSignup = (ctaName: string, section: string) => {
    trackEvent('for_pro_cta_click', {
      cta_name: ctaName,
      section,
      destination: 'sign_up',
    });
    navigate('/sign-up?role=handyman', { state: { from: location.pathname } });
  };

  return (
    <Container
      sx={{
        pt: { xs: '96px', sm: '136px' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '956px',
          maxWidth: '100%',
          gap: { xs: '24px', sm: '48px' },
          px: 0,
          mb: { xs: '24px', sm: '48px' },
          mx: 'auto',
        }}
      >
        {/* Heading Section */}
        <BoxInfoWrapper>
          <Heading sx={{ mb: '24px' }} variant='h1'>
            Stop paying for leads.
            <br /> Focus on your craft.
          </Heading>
          <Typography
            sx={{
              fontFamily: 'Rodoto, sans-serif',
              color: isDark
                ? GlassColors.dark.textPrimary
                : GlassColors.light.textPrimary,
              fontWeight: 700,
              fontSize: { xs: '16px', md: '20px' },
              lineHeight: '175%',
              letterSpacing: '.15px',
              textAlign: 'center',
              mb: { xs: '24px', md: '32px' },
            }}
          >
            No credit packs, hidden rules, or complex dashboards. We built WOM
            because we value your expertise. You focus on doing the work you’ve
            mastered, and we’ll take care of the admin. Browse local jobs and
            connect with clients instantly—with zero upfront fees.
          </Typography>
          <NeumorphicButton
            id='btn-reg-1'
            sx={{
              display: 'flex',
              gap: '8px',
              m: '0 auto 16px',
              fontSize: '16px',
            }}
            onClick={() => goHandymanSignup('create_free_profile', 'hero')}
            data-ga-label='Create Your Free Profile'
          >
            <CaseIcon />
            Create Your Free Profile
          </NeumorphicButton>
          <HelperText>Takes 2 minutes. No credit card required.</HelperText>
        </BoxInfoWrapper>

        {/* Comparing Block Section */}
        <BoxInfoWrapper>
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              textAlign: 'center',
              fontWeight: { xs: 700, tablet: 600 },
              fontSize: { xs: 24, md: 34 },
              lineHeight: { xs: '133%', tablet: '124%' },
              letterSpacing: { xs: 0, tablet: '.25px' },
              color: isDark ? GlassColors.accent : GlassColors.secondary,
              mb: { xs: '24px', tablet: '16px' },
              maxWidth: { xs: 352, tablet: 'none' },
              mx: 'auto',
            }}
          >
            Why local pros are choosing WOM
          </Typography>
          <CompareBlock />
          <NeumorphicButton
            id='btn-reg-2'
            sx={{
              display: 'flex',
              gap: '8px',
              m: '32px auto 16px',
              fontSize: '16px',
            }}
            onClick={() => goHandymanSignup('join_as_pro', 'compare_block')}
            data-ga-label='Join as a Pro'
          >
            <CaseIcon />
            Join as a Pro
          </NeumorphicButton>
          <HelperText>Setup takes less than 2 minutes.</HelperText>
        </BoxInfoWrapper>

        {/* Path to Start Section */}
        <BoxInfoWrapper>
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              textAlign: 'center',
              fontWeight: { xs: 700, tablet: 600 },
              fontSize: { xs: 24, md: 34 },
              lineHeight: { xs: '133%', tablet: '124%' },
              letterSpacing: { xs: 0, tablet: '.25px' },
              color: isDark ? GlassColors.accent : GlassColors.secondary,
              mb: { xs: '24px', tablet: '32px' },
            }}
          >
            Your path to getting local jobs
          </Typography>
          <StepsPath isDark={isDark} />
          <NeumorphicButton
            id='btn-reg-3'
            sx={{
              display: 'flex',
              gap: '8px',
              m: '32px auto 16px',
              fontSize: '16px',
            }}
            onClick={() => goHandymanSignup('start_getting_jobs', 'steps_path')}
            data-ga-label='Start Getting Jobs'
          >
            <CaseIcon />
            Start Getting Jobs
          </NeumorphicButton>
          <HelperText>Free to join. Free to quote.</HelperText>
        </BoxInfoWrapper>

        <BoxInfoWrapper
          sx={{
            boxShadow: `
              14.15px 14.15px 22.64px -0.37px #00FFFF21,
              -17.54px -17.54px 28.66px 0px #A6FFFF30,
              10px 10px 28.66px -11.7px #00929233,
              1.58px 1.58px 1.7px 0px #A3FFFF inset,
              -2.07px -2.64px 4.52px -1.51px #00929252 inset
            `,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              textAlign: 'center',
              fontWeight: { xs: 700, tablet: 600 },
              fontSize: { xs: 24, md: 34 },
              lineHeight: { xs: '133%', tablet: '124%' },
              letterSpacing: { xs: 0, tablet: '.25px' },
              color: isDark ? GlassColors.accent : GlassColors.secondary,
              mb: { xs: '24px', tablet: '16px' },
            }}
          >
            Ready to work on your own terms?
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Rodoto, sans-serif',
              color: isDark
                ? GlassColors.dark.textPrimary
                : GlassColors.light.textPrimary,
              fontWeight: 700,
              fontSize: { xs: '16px', md: '20px' },
              lineHeight: '175%',
              letterSpacing: '.15px',
              textAlign: 'center',
              mb: { xs: '24px', md: '32px' },
            }}
          >
            Join the fairest network of trusted local professionals in the UK.
            Focus on what you do best, and let us bring the clients to you. Zero
            upfront costs, just fair work.
          </Typography>
          <NeumorphicButton
            id='btn-reg-4'
            sx={{
              display: 'flex',
              gap: '8px',
              m: '0 auto 16px',
              fontSize: '16px',
            }}
            onClick={() =>
              goHandymanSignup('join_as_professional', 'final_cta')
            }
            data-ga-label='Join as a Professional'
          >
            <CaseIcon />
            Join as a Professional
          </NeumorphicButton>
          <HelperText sx={{ p: '0 20px' }}>
            100% free to join and quote. We only earn when you do.
          </HelperText>
        </BoxInfoWrapper>
      </Box>
    </Container>
  );
};

// BoxInfoWrapper
const BoxInfoWrapper = styled(Box)(({ theme }) => ({
  padding: '32px 95px',
  borderRadius: '64px',
  boxShadow: GlassShadows.cardShadowDefault,

  [theme.breakpoints.down('md')]: {
    padding: '32px 24px',
  },
}));

// Heading
const Heading = styled(Typography)(({ theme }) => ({
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
  fontSize: '60px',
  lineHeight: '120%',
  textAlign: 'center',
  letterSpacing: '-0.5px',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',

  [theme.breakpoints.down('md')]: {
    fontSize: '48px',
    lineHeight: 1.24,
    letterSpacing: '.25px',
    fontWeight: 600,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '34px',
  },
}));

// Helper text
const HelperText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
  letterSpacing: '.4px',
  fontSize: '12px',
  lineHeight: '166%',
  color:
    theme.palette.mode === 'dark'
      ? GlassColors.dark.textSecondary
      : GlassColors.light.textSecondary,
  textAlign: 'center',
}));

export default ForProPage;
